export function normalizeAnswer(value: string): string {
  return value.trim().toLowerCase().replaceAll(/\s+/g, " ");
}

/**
 * Normalize a typed meaning answer so lowercase ASCII letters and spaces
 * participate in validation. Every other character is optional without
 * changing gloss search normalization or pronunciation validation.
 */
export function normalizeTypedMeaningAnswer(value: string): string {
  return normalizeAnswer(value)
    .replaceAll(/[^a-z ]/g, "")
    .replaceAll(/\s+/g, " ")
    .trim();
}

/**
 * Produce the accepted normalizations of an expected meaning answer. Besides
 * omitting punctuation, punctuation may stand in for a word boundary, so a
 * back such as "what-how" accepts both "whathow" and "what how".
 */
export function normalizeTypedMeaningAnswerVariants(value: string): string[] {
  const withoutPunctuation = normalizeTypedMeaningAnswer(value);
  const withPunctuationAsSpaces = normalizeAnswer(value)
    .replaceAll(/[^a-z ]/g, " ")
    .replaceAll(/\s+/g, " ")
    .trim();

  return withPunctuationAsSpaces === withoutPunctuation
    ? [withoutPunctuation]
    : [withoutPunctuation, withPunctuationAsSpaces];
}

// Card backs often carry a parenthetical qualifier, e.g. "bunch (of people)",
// and verb glosses are written with a leading "to ", e.g. "to combine". We
// accept the full phrase plus relaxed variants:
//   - parenthetical(s) stripped: "bunch (of people)" -> "bunch"
//   - leading "to " dropped:     "to combine"         -> "combine"
//   - leading "a " dropped:      "a lot of"           -> "lot of"
//   - trailing " of" then dropped from that article-less variant:
//                                "a lot of"           -> "lot"
//   - "particle" dropped:        "completion particle" -> "completion"
// so the user doesn't have to type the qualifier, infinitive marker, or the
// generic grammatical category when the rest of the gloss identifies it.
export function answerVariants(value: string): string[] {
  const variants = new Set<string>();
  const add = (candidate: string) => {
    const normalized = normalizeAnswer(candidate);
    if (normalized.length > 0) {
      variants.add(normalized);
    }
  };

  for (const candidate of [value, value.replaceAll(/\([^()]*\)/g, " ")]) {
    const normalized = normalizeAnswer(candidate);
    for (const particleVariant of [normalized, normalized.replaceAll(/\bparticle\b/g, " ")]) {
      const normalizedParticleVariant = normalizeAnswer(particleVariant);
      const prefixVariants = [normalizedParticleVariant];
      if (normalizedParticleVariant.startsWith("to ")) {
        prefixVariants.push(normalizedParticleVariant.slice("to ".length));
      }

      for (const prefixVariant of prefixVariants) {
        add(prefixVariant);
        if (prefixVariant.startsWith("a ")) {
          const withoutArticle = prefixVariant.slice("a ".length);
          add(withoutArticle);
          if (withoutArticle.endsWith(" of")) {
            add(withoutArticle.slice(0, withoutArticle.length - " of".length));
          }
        }
      }
    }
  }

  return [...variants];
}
